import React from 'react'
import styled from 'styled-components'

import { useLangStoreData } from '../utils/LangStore'

import webImg from '../assets/icons/web.png'
import robotImg from '../assets/icons/robot.png'
import cloudImg from '../assets/icons/cloud.png'
import appsImg from '../assets/icons/apps.png'
import analysisIcon from '../assets/icons/analysis-icon.svg'
import deliveryIcon from '../assets/icons/delivery-icon.svg'
import developmentIcon from '../assets/icons/development-icon.svg'
import validationIcon from '../assets/icons/validation-icon.svg'

import newnoteLogo from '../assets/icons/newnoteLogo.png'
import gscLogo from '../assets/icons/gscLogo.svg'
import guerinLogo from '../assets/icons/geLogo.png'
import isqeLogo from '../assets/icons/isqeLogo.png'
import crcLogo from '../assets/icons/crcLogo.png'
import mrLogo from '../assets/icons/mrLogo.png'
import driveonholidaysLogo from '../assets/icons/driveonholidays-logo.png'
import drivaliaLogo from '../assets/icons/drivalia-logo.png'
import graceLogo from '../assets/icons/graceLogo.png'
import choiceLogo from '../assets/icons/choiceLogo.png'
import top5Logo from '../assets/icons/top5Logo.png'

import Page from '../components/Page'
import { ROUTES, UI } from '../constants'

import ImageContainer from '../components/image-container/ImageContainer'

import bannerBigSrc from '../assets/pics/home-page-2.png'
import bannerSmallSrc from '../assets/pics/home-page-2-small.jpg'

const HomePage = () => (
  <Page>
    <HomePageBanner />
    <HomePageServices />
    <HomePageProcess />
    <HomePageClients />
    <HomePageContactUs />
  </Page>
)

export default HomePage

const Banner = styled.section`
  height: 800px;
  overflow: hidden;
  position: relative;
  width: 100%;

  @media (max-width: ${UI.MOBILE}) {
    height: 120vh;
    min-height: 600px;
  }
`

const ParticlesContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
`

const Particle = styled.div`
  position: absolute;
  width: 4px;
  height: 4px;
  background: rgba(60, 163, 213, 0.6);
  border-radius: 50%;
  animation: float ${props => props.duration}s infinite;
  animation-delay: ${props => props.delay}s;

  @keyframes float {
    0%, 100% {
      transform: translate(0, 0) scale(1);
      opacity: 0;
    }
    10% {
      opacity: 1;
    }
    90% {
      opacity: 1;
    }
    100% {
      transform: translate(100px, -1000px) scale(0);
      opacity: 0;
    }
  }
`

const Content = styled.div`
  height: 100%;
  display: block;
  margin: 0 auto;
  padding: 20px;
  position: relative;
  z-index: 10;

  @media (max-width: ${UI.MOBILE}) {
    padding: 15px;
  }
`

const Center = styled.div`
  margin-top: var(--s0);
  position: relative;
  top: 50%;
  transform: translateY(-50%);
  color: white;
  display: block;

  @media (max-width: ${UI.MOBILE}) {
    & {
      gap: 0;
      top: 45%;
      padding: 0 10px;
    }
  }
`

const BannerText = styled.h1`
  text-align: center;
  color: white;
  line-height: 1.5;
  font-size: 54px;
  font-weight: bold;
  letter-spacing: 2px;
  animation: fadeInUp 1s ease backwards;
  animation-delay: ${props => props.delay || '0s'};

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: ${UI.TABLET}) {
    & {
      font-size: 32px;
    }
  }
  
  @media (max-width: ${UI.MOBILE}) {
    & {
      text-align: center;
      font-size: 24px !important;
      letter-spacing: 1px;
      line-height: 1.3;
    }
  }
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
  margin-top: 48px;
  animation: fadeInUp 1s ease backwards;
  animation-delay: 0.4s;

  @media (max-width: ${UI.MOBILE}) {
    gap: 15px;
    margin-top: 32px;
  }
`

const BannerButton = styled.a`
  display: inline-block;
  font-weight: 700;
  width: max-content;
  border: 2px solid var(--color-blue-cta);
  border-radius: 50px;
  padding: 18px 40px;
  text-transform: uppercase;
  font-size: 20px;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.3s ease;

  color: ${props => props.secondary ? 'var(--color-blue-cta)' : 'var(--color_neutral_lightest)'};
  background: ${props => props.secondary ? 'transparent' : 'var(--color-blue-cta)'};
  border-color: ${props => props.secondary ? 'white' : 'var(--color-blue-cta)'};
  
  ${props => !props.secondary && `
    -webkit-box-shadow: 0 0em 1em rgb(60 163 213 / 30%);
    box-shadow: 0 0em 1em rgb(60 163 213 / 30%);
  `}

  &:hover {
    transform: translateY(-3px);
    ${props => props.secondary ? `
      background: white;
      color: var(--color-blue-dark);
      box-shadow: 0 10px 30px rgba(255, 255, 255, 0.3);
    ` : `
      box-shadow: 0 15px 40px rgba(60, 163, 213, 0.6);
      transform: translateY(-3px) scale(1.02);
    `}
  }

  @media (max-width: ${UI.TABLET}) {
    & {
      padding: 15px 32px;
      font-size: 18px;
    }
  }

  @media (max-width: ${UI.MOBILE}) {
    & {
      padding: 12px 24px;
      font-size: 16px;
      width: 100%;
      max-width: 280px;
    }
  }
`

const FeatureCards = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  max-width: 1000px;
  margin: 80px auto 0;
  padding: 0 20px;
  animation: fadeInUp 1s ease backwards;
  animation-delay: 0.6s;

  @media (max-width: ${UI.TABLET}) {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-top: 60px;
  }

  @media (max-width: ${UI.MOBILE}) {
    margin-top: 40px;
    margin-bottom: 60px;
    padding: 0 10px;
    gap: 12px;
  }
`

const FeatureCard = styled.div`
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  padding: 24px;
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(60, 163, 213, 0.5);
    box-shadow: 0 10px 30px rgba(60, 163, 213, 0.2);
  }

  @media (max-width: ${UI.MOBILE}) {
    padding: 16px;
    border-radius: 15px;
  }
`

const FeatureIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 12px;
`

const FeatureTitle = styled.h3`
  color: white;
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 8px;
  letter-spacing: 1px;

  @media (max-width: ${UI.MOBILE}) {
    font-size: 16px;
  }
`

const FeatureText = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  line-height: 1.5;
  margin: 0;

  @media (max-width: ${UI.MOBILE}) {
    font-size: 13px;
  }
`

// Generate random particles data
const generateParticles = (count) => {
  return Array.from({ length: count }, () => ({
    left: Math.random() * 100,
    duration: Math.random() * 10 + 15,
    delay: Math.random() * 20
  }))
}

const HomePageBanner = () => {
  const { t } = useLangStoreData()
  const isMobile = React.useMemo(() => window.innerWidth <= 480, [])
  const particles = React.useMemo(() => generateParticles(isMobile ? 20 : 50), [isMobile])

  return (
    <Banner>
      <ParticlesContainer>
        {particles.map((particle, index) => (
          <Particle
            key={index}
            style={{ left: `${particle.left}%` }}
            duration={particle.duration}
            delay={particle.delay}
          />
        ))}
      </ParticlesContainer>
      
      <ImageContainer
        src={bannerBigSrc}
        thumb={bannerSmallSrc}
        alt={'Banner'}
        onIsVisible={() => true}
      />
      
      <Content className='container'>
        <Center>
          <div>
            <BannerText delay="0s" style={{ marginTop: '65px' }}>
              {t('homepage__bannerText1')}
            </BannerText>
            <BannerText delay="0.2s" style={{ marginTop: '30px' }}>
              {t('homepage__bannerText2')}
            </BannerText>
            
            <ButtonContainer>
              <BannerButton href={ROUTES.contactUsPage}>
                {t('homepage__bannerCTA')}
              </BannerButton>
              <BannerButton href={ROUTES.aboutUsPage} secondary>
                {t('homepage__bannerCTA2')}
              </BannerButton>
            </ButtonContainer>

            <FeatureCards>
              <FeatureCard>
                <FeatureTitle>{t('homepage__feature1Title') || 'Soluções Personalizadas'}</FeatureTitle>
                <FeatureText>
                  {t('homepage__feature1Text')}
                </FeatureText>
              </FeatureCard>
              
              <FeatureCard>
                <FeatureTitle>{t('homepage__feature2Title')}</FeatureTitle>
                <FeatureText>
                  {t('homepage__feature2Text')}
                </FeatureText>
              </FeatureCard>
              
              <FeatureCard>
                <FeatureTitle>{t('homepage__feature3Title')}</FeatureTitle>
                <FeatureText>
                  {t('homepage__feature3Text')}
                </FeatureText>
              </FeatureCard>
            </FeatureCards>
          </div>
        </Center>
      </Content>
    </Banner>
  )
}

const Services = styled.section`
  @media (max-width: ${UI.MOBILE}) {
    padding-top: 120px;
  }
`

const ServicesIntro = styled.div`
  text-align: center;
  font-size: 28px;
  margin: 0 auto;

  @media (max-width: ${UI.TABLET}) {
    font-size: 24px;
  }

  @media (max-width: ${UI.MOBILE}) {
    font-size: 20px;
    padding: 0 15px;
  }
`

const ServicesTitle = styled.h2`
  padding-top: var(--s3);
`

const SERVICES_GRID_TWO_COLUMNS_RESOLUTION = '1060px'
const SERVICES_GRID_ONE_COLUMN_RESOLUTION = '522px'

const ServicesContent = styled.div`
  margin-top: var(--s2);

  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  justify-items: center;
  grid-template-rows: 1fr;
  row-gap: 64px;

  @media (max-width: ${SERVICES_GRID_TWO_COLUMNS_RESOLUTION}) {
    & {      
      grid-template-columns: max-content max-content;
      justify-content: center;
      column-gap: 12vw;
    }
  }

  @media (max-width: ${SERVICES_GRID_ONE_COLUMN_RESOLUTION}) {
    & {
      justify-items: center;
      grid-template-columns: 1fr;
      row-gap: 48px;
    }
  }

  @media (max-width: ${UI.MOBILE}) {
    & {
      row-gap: 32px;
    }
  }
`

const ServicesCTA = styled.a`
  margin-top: var(--s2);
`

const HomePageServices = () => {
  const { t } = useLangStoreData()
  return (
    <Services className='container section'>
      <ServicesIntro className='title'><p dangerouslySetInnerHTML={{ __html: t('homepage__companyIntro') }} /></ServicesIntro>
      <ServicesTitle className='section-title'>{t('homepage__servicesTitle')}</ServicesTitle>
      <ServicesContent>
        <ServicesItem text={t('homepage__servicesWeb')} imgSrc={webImg} />
        <ServicesItem text={t('homepage__servicesAutomation')} imgSrc={robotImg} />
        <ServicesItem text={t('homepage__servicesCloud')} imgSrc={cloudImg} />
        <ServicesItem text={t('homepage__servicesMobile')} imgSrc={appsImg} />
      </ServicesContent>
      <ServicesCTA href={ROUTES.servicesPage} className='section-cta-btn' style={{ borderRadius: '40px' }}>{t('homepage__servicesCTA')}</ServicesCTA>
    </Services>
  )
}

const ServicesItemStyled = styled.div`
  width: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: ${UI.MOBILE}) {
    width: 120px;
  }
`

const ServicesItemImage = styled.img`
  width: 100px;

  @media (max-width: ${UI.MOBILE}) {
    width: 80px;
  }
`

const ServicesitemLabel = styled.div`
  text-align: center;
  margin-top: 8px;
  font-weight: 700;
  font-size: 24px;

  @media (max-width: ${UI.MOBILE}) {
    & {
      font-size: 18px;
    }
  }
`

const ServicesItem = ({ text, imgSrc }) => {
  return (
    <ServicesItemStyled>
      <ServicesItemImage src={imgSrc} alt='Services Image' />
      <ServicesitemLabel>{text}</ServicesitemLabel>
    </ServicesItemStyled>
  )
}

const Processes = styled.section`
  background-color: var(--color-blue-dark);
`

const ProcessesSection = styled.section`
  padding: 0px;
`

const ProcessesTitle = styled.h2`
  color: white;
`

const PROCESSES_GRID_TWO_COLUMNS_RESOLUTION = '1240px'
const PROCESSES_GRID_ONE_COLUMN_RESOLUTION = '670px'

const ProcessesContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  grid-template-rows: 1fr;
  justify-items: center;
  row-gap: 64px;
  padding-top: var(--s2);

  @media (max-width: ${PROCESSES_GRID_TWO_COLUMNS_RESOLUTION}) {
    & {
      grid-template-columns: max-content max-content;
      justify-content: center;
      column-gap: 8vw;
    }
  }

  @media (max-width: ${PROCESSES_GRID_ONE_COLUMN_RESOLUTION}) {
    & {
      justify-items: center;
      grid-template-columns: max-content;
      row-gap: 48px;
    }
  }

  @media (max-width: ${UI.MOBILE}) {
    & {
      row-gap: 32px;
    }
  }
`

const ProcessesCTA = styled.a`
  margin-top: var(--s2);
`

const HomePageProcess = () => {
  const { t } = useLangStoreData()
  return (
    <Processes className='section'>
      <ProcessesSection className='container'>
        <ProcessesTitle className='section-title'>{t('homepage__processesTitle')}</ProcessesTitle>
        <ProcessesContent>
          <ProcessesItem title={t('homepage__processesAnalysis')} description={t('homepage__processesAnalysisText')} imgSrc={analysisIcon} />
          <ProcessesItem title={t('homepage__processesDevelopment')} description={t('homepage__processesDevelopmentText')} imgSrc={developmentIcon} />
          <ProcessesItem title={t('homepage__processesValidation')} description={t('homepage__processesValidationText')} imgSrc={validationIcon} />
          <ProcessesItem title={t('homepage__processesDelivery')} description={t('homepage__processesDeliveryText')} imgSrc={deliveryIcon} />
        </ProcessesContent>
      </ProcessesSection>
      <ProcessesCTA href={ROUTES.processPage} className='section-cta-btn' style={{ borderRadius: '40px' }}>{t('homepage__processesCTA')}</ProcessesCTA>
    </Processes>
  )
}

const ProcessesItemStyled = styled.div`
  display: flex;
  width: 300px;
  flex-direction: column;  
  align-items: center;
  gap: 15px;

  @media (max-width: ${UI.MOBILE}) {
    width: 100%;
    max-width: 280px;
    padding: 0 20px;
  }
`

const ProcessesItemImage = styled.img`
  width: 100px;

  @media (max-width: ${UI.MOBILE}) {
    width: 80px;
  }
`

const ProcessesItemTitle = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: white;
  letter-spacing: 3px;

  @media (max-width: ${UI.MOBILE}) {
    & {
      font-size: 20px;
      letter-spacing: 1px;   
    }
  }
`

const ProcessesItemDescription = styled.div`
  font-style: 20px;
  color: white;
  text-align: center;

  @media (max-width: ${UI.MOBILE}) {
    & {
      font-size: 18px;
    }
  }
`

const ProcessesItem = ({ title, description, imgSrc }) => {
  return (
    <ProcessesItemStyled>
      <ProcessesItemImage src={imgSrc} alt='Process Image' />
      <ProcessesItemTitle>{title}</ProcessesItemTitle>
      <ProcessesItemDescription>{description}</ProcessesItemDescription>
    </ProcessesItemStyled>
  )
}

const Clients = styled.section`
`

const Recognition = styled.section`
  background-color: var(--color-blue-light);
`

const ClientsContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 64px 128px;
  align-items: center;

  padding-top: var(--s2);

  @media (max-width: ${UI.TABLET}) {
    gap: 48px 64px;
  }

  @media (max-width: ${UI.MOBILE}) {
    flex-direction: column;
    gap: 32px;
    padding: 0 20px;
  }
`

const ClientsLogo = styled.img`
  max-width: 210px;
  max-height: 100px;

  @media (max-width: ${UI.MOBILE}) {
    max-width: 150px;
  }
`

const HomePageClients = () => {
  const { t } = useLangStoreData()
  return (
    <>
      <Clients className='section'>
        <h2 className='section-title'>{t('homepage__clientsTitle')}</h2>
        <ClientsContent>
          <ClientsLogo url='https://salvadorcaetano.pt/' src={gscLogo} alt='GSC Logo' style={{ filter: 'grayscale(1)' }} />
          <ClientsLogo url='https://www.guerin.pt/' src={guerinLogo} alt='Guerin Logo' style={{ filter: 'grayscale(1)' }} />
          <ClientsLogo url='https://www.newnote.pt/' src={newnoteLogo} alt='New Note Logo' style={{ filter: 'brightness(0.5)' }} />
          {/* <ClientsLogo url='https://tangerinerent.com/' src={tangerineLogo} alt='Tangerine Logo' style={{ filter: 'brightness(0.5)' }} /> */}

          <ClientsLogo url='https://www.isqe.com/' src={isqeLogo} alt='ISQe Logo' style={{ filter: 'grayscale(1)' }} />
          <ClientsLogo url='https://www.crcrental.pt/' src={crcLogo} alt='CRC Logo' style={{ filter: 'brightness(0.5)' }} />
          <ClientsLogo url='https://www.madeirarent.pt/' src={mrLogo} alt='Madeira Rent Logo' style={{ filter: 'brightness(0.5)' }} />
          <ClientsLogo url='https://www.driveonholidays.com/pt/home' src={driveonholidaysLogo} alt='Drive On Holidays Logo' style={{ filter: 'grayscale(1)' }} />
          <ClientsLogo url='https://www.drivalia.pt/?gad_source=1&gad_campaignid=23292729346&gbraid=0AAAAApY-3E74nhenvjFYDoxyWOvD3zpno&gclid=CjwKCAiA64LLBhBhEiwA-Pxgu2WVpS6oPzOyUraBE8XQOssKJXvG3C_J_EB6u2mrWmbagcjGhqA9iRoCSRYQAvD_BwE' src={drivaliaLogo} alt='Drivalia Logo' style={{ filter: 'grayscale(1)' }} />
          <ClientsLogo url='https://www.choicecar.pt/' src={choiceLogo} alt='Choice Car Logo' style={{ filter: 'grayscale(1)' }} />
        </ClientsContent>
      </Clients>

      <Recognition className='section'>
        <h2 className='section-title'>{t('homepage__recognitionTitle')}</h2>
        <ClientsContent>
          <ClientsLogo url='https://grace.pt/' src={graceLogo} alt='Grace Logo' style={{ filter: 'grayscale(0)', maxWidth: '360px', maxHeight: '180px' }} />
          <ClientsLogo src={top5Logo} alt='Top 5 Logo' style={{ filter: 'grayscale(0)', maxWidth: '320px', maxHeight: '160px' }} />
        </ClientsContent>
      </Recognition>
    </>
  )
}

const ContactUs = styled.section`
  background-color: var(--color-blue);
`

const ContactUsText = styled.div`
  line-height: 1.5;
  text-align: center;
  font-size: 32px;
  margin: 0 auto;
  color: white;

  @media (max-width: ${UI.TABLET}) {
    & {
      font-size: 26px;
    }
  }

  @media (max-width: ${UI.MOBILE}) {
    & {
      font-size: 22px;
      padding: 0 15px;
    }
  }
`

const ContactUsCTA = styled.a`
  margin-top: var(--s2);
`

const HomePageContactUs = () => {
  const { t } = useLangStoreData()
  return (
    <ContactUs className='section'>
      <ContactUsText dangerouslySetInnerHTML={{ __html: t('homepage__ContactUsText1') }} />
      <ContactUsText dangerouslySetInnerHTML={{ __html: t('homepage__ContactUsText2') }} />
      <ContactUsCTA className='section-cta-btn' href={ROUTES.contactUsPage} style={{ borderRadius: '40px' }}>{t('homepage__ContactUsCTA')}</ContactUsCTA>
    </ContactUs>
  )
}
